/**
 * The theme editor's session state: which theme is being edited, what seeded
 * it, and which appearance the panel opened on.
 *
 * Adapted from upstream's `themeEditorStore.ts` (pingdotgg/t3code, MIT, pin
 * e3e7cc3f). The store lives above the router so a draft survives
 * navigation: the palette is painted on the live app, and judging it means
 * browsing the app while the editor stays open. Sessions are numbered so
 * reopening while open reseeds the panel instead of being read as a no-op.
 *
 * Upstream's toggle derives the seed from the active theme through its
 * palette table; forge's callers already hold the theme state, so they pass
 * the seed in.
 *
 * @module
 */

import { create } from "zustand";

import type { ThemeAppearance } from "@cmmd-forge/contracts/theme";

export interface ThemeEditorSessionInput {
  /** Set when editing an installed theme; null creates a new one. */
  readonly editingThemeId: string | null;
  /** Theme a new theme starts from: the active one, or a duplicate target. */
  readonly seedThemeId: string | null;
  /** Prefilled name, used by duplicate. */
  readonly seedName: string | null;
  readonly initialAppearance: ThemeAppearance;
}

interface ThemeEditorSession extends ThemeEditorSessionInput {
  readonly id: number;
}

interface ThemeEditorStore {
  readonly session: ThemeEditorSession | null;
  readonly openThemeEditor: (input: ThemeEditorSessionInput) => void;
  readonly closeThemeEditor: () => void;
}

let nextSessionId = 0;

export const useThemeEditorStore = create<ThemeEditorStore>((set) => ({
  session: null,
  openThemeEditor: (input) => set({ session: { ...input, id: ++nextSessionId } }),
  closeThemeEditor: () => set({ session: null }),
}));

/** The row action on a theme: open its editor, or close it if already open. */
export function toggleThemeEditor(input: ThemeEditorSessionInput): void {
  const store = useThemeEditorStore.getState();
  if (store.session) {
    store.closeThemeEditor();
    return;
  }
  store.openThemeEditor(input);
}

/**
 * Open (or reseed) a session from outside React: the settings section's row
 * actions have no store subscription of their own.
 */
export function openThemeEditor(input: ThemeEditorSessionInput): void {
  useThemeEditorStore.getState().openThemeEditor(input);
}
